/**
 * Charge un questionnaire depuis Supabase (questionnaires + axes + questions + options)
 * et le convertit au format legacy attendu par le composant Assessment générique.
 *
 * Format de sortie:
 * - data: Array<{ id, category, question, responses, scores? }>
 * - categoryMapping: Record<axisName, axisName>
 * - axes: meta des axes (slug, name, weight, color, is_scored)
 * - loading, error
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface QuestionnaireAxisMeta {
  id: string;
  slug: string;
  name: string;
  weight: number;
  is_scored: boolean;
  color: string | null;
  display_order: number;
  description: string | null;
}

export interface LegacyQuestion {
  id: number;          // index numérique stable pour compat (clé d'answers)
  dbId: string;        // UUID question (pour score pondéré BDD)
  code: string;
  category: string;
  question: string;
  responses: string[];
  scores?: number[];
}

export interface QuestionnaireFromDB {
  data: LegacyQuestion[];
  categoryMapping: Record<string, string>;
  axes: QuestionnaireAxisMeta[];
}

interface State {
  questionnaire: QuestionnaireFromDB | null;
  loading: boolean;
  error: string | null;
}

export const useQuestionnaireFromDB = (slug: string) => {
  const [state, setState] = useState<State>({
    questionnaire: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    if (!slug) {
      setState({ questionnaire: null, loading: false, error: null });
      return () => { cancelled = true; };
    }

    const load = async () => {
      try {
        setState(s => ({ ...s, loading: true, error: null }));

        const { data: q, error: qErr } = await supabase
          .from('questionnaires')
          .select('id')
          .eq('slug', slug)
          .eq('status', 'published')
          .maybeSingle();

        if (qErr) throw qErr;
        if (!q) throw new Error(`Questionnaire "${slug}" introuvable`);

        const { data: axes, error: aErr } = await supabase
          .from('questionnaire_axes')
          .select('id, slug, name, weight, is_scored, color, display_order, description')
          .eq('questionnaire_id', q.id)
          .order('display_order', { ascending: true });

        if (aErr) throw aErr;
        if (!axes || axes.length === 0) throw new Error('Aucun axe pour ce questionnaire');

        const axisIds = axes.map(a => a.id);

        const { data: questions, error: quErr } = await supabase
          .from('questions')
          .select('id, axis_id, code, label, type, display_order, is_scored')
          .in('axis_id', axisIds)
          .order('display_order', { ascending: true });

        if (quErr) throw quErr;
        if (!questions) throw new Error('Aucune question');

        const questionIds = questions.map(q => q.id);
        const { data: options, error: oErr } = await supabase
          .from('question_options')
          .select('id, question_id, label, value, score, display_order')
          .in('question_id', questionIds)
          .order('display_order', { ascending: true });

        if (oErr) throw oErr;

        // Index axes
        const axisById = new Map(axes.map(a => [a.id, a]));

        // Tri global: ordre axe puis ordre question
        const sorted = [...questions].sort((a, b) => {
          const axA = axisById.get(a.axis_id)?.display_order ?? 0;
          const axB = axisById.get(b.axis_id)?.display_order ?? 0;
          if (axA !== axB) return axA - axB;
          return a.display_order - b.display_order;
        });

        const optionsByQuestion = new Map<string, typeof options>();
        (options ?? []).forEach(o => {
          const arr = optionsByQuestion.get(o.question_id) ?? [];
          arr.push(o);
          optionsByQuestion.set(o.question_id, arr);
        });

        const data: LegacyQuestion[] = sorted.map((qu, idx) => {
          const axis = axisById.get(qu.axis_id);
          const opts = (optionsByQuestion.get(qu.id) ?? []).slice().sort(
            (a, b) => a.display_order - b.display_order
          );
          const responses = opts.map(o => o.label);
          const scores = axis?.is_scored && qu.is_scored
            ? opts.map(o => Number(o.score) || 0)
            : undefined;

          return {
            id: idx + 1,
            dbId: qu.id,
            code: qu.code,
            category: axis?.name ?? 'Autre',
            question: qu.label,
            responses,
            ...(scores ? { scores } : {}),
          };
        });

        const categoryMapping: Record<string, string> = {};
        axes.forEach(a => { categoryMapping[a.name] = a.name; });

        if (cancelled) return;

        setState({
          questionnaire: {
            data,
            categoryMapping,
            axes: axes.map(a => ({
              id: a.id,
              slug: a.slug,
              name: a.name,
              weight: Number(a.weight) || 0,
              is_scored: a.is_scored,
              color: a.color,
              display_order: a.display_order,
              description: (a as any).description ?? null,
            })),
          },
          loading: false,
          error: null,
        });
      } catch (e: any) {
        if (cancelled) return;
        console.error('[useQuestionnaireFromDB] load error', e);
        setState({
          questionnaire: null,
          loading: false,
          error: e?.message ?? 'Erreur chargement questionnaire',
        });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [slug]);

  return state;
};
